import type { ReportData, ResultSegment } from "../types";

/**
 * Format a number as percentage
 * @param value
 * @param decimals
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a number with decimals
 * @param value
 * @param decimals
 */
export function formatDecimal(value: number, decimals = 2): string {
  return value.toFixed(decimals);
}

/**
 * Format a date in Brazilian format
 * @param date
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
}

/**
 * Format a date with time in Brazilian format
 * @param date
 */
export function formatDateTime(date: Date): string {
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

/**
 * Get color for a score value (0-100)
 * @param score
 */
export function getScoreColor(score: number): string {
  if (score >= 80) return "#22c55e"; // green
  if (score >= 60) return "#84cc16"; // lime
  if (score >= 40) return "#eab308"; // yellow
  if (score >= 20) return "#f97316"; // orange
  return "#ef4444"; // red
}

/**
 * Get performance label for a score
 * @param score
 */
export function getScoreLabel(score: number): string {
  if (score >= 80) return "Excelente";
  if (score >= 60) return "Bom";
  if (score >= 40) return "Regular";
  if (score >= 20) return "Abaixo da média";
  return "Crítico";
}

/**
 * Calculate average of result segments
 * @param segments
 */
export function calculateAverage(segments: ResultSegment[]): number {
  if (segments.length === 0) return 0;

  let sum = 0;
  for (const segment of segments) {
    sum += segment.value;
  }
  return sum / segments.length;
}

/**
 * Find highest scoring dimension
 * @param segments
 */
export function findHighestDimension(segments: ResultSegment[]): ResultSegment | null {
  if (segments.length === 0) return null;

  let highest = segments[0]!;
  for (const segment of segments) {
    if (segment.value > highest.value) {
      highest = segment;
    }
  }
  return highest;
}

/**
 * Find lowest scoring dimension
 * @param segments
 */
export function findLowestDimension(segments: ResultSegment[]): ResultSegment | null {
  if (segments.length === 0) return null;

  let lowest = segments[0]!;
  for (const segment of segments) {
    if (segment.value < lowest.value) {
      lowest = segment;
    }
  }
  return lowest;
}

/**
 * Map report data to template-friendly format
 * @param data
 */
export function mapReportDataForTemplate(data: ReportData) {
  const empresaAverage = calculateAverage(data.resultados.empresa);
  const geralAverage = data.resultados.geral ? calculateAverage(data.resultados.geral) : null;

  const highest = findHighestDimension(data.resultados.empresa);
  const lowest = findLowestDimension(data.resultados.empresa);

  return {
    // Company info
    empresaNome: data.empresa.nomeFantasia || data.empresa.razaoSocial,
    empresaCnpj: data.empresa.cnpj,
    empresaPorte: getPorteLabel(data.empresa.porte),
    empresaSetor: data.empresa.cnae,

    // Questionnaire info
    questionarioNome: data.questionario.nome,
    questionarioDescricao: data.questionario.descricao,
    projetoNome: data.projeto.nome,

    // Results
    mediaEmpresa: empresaAverage,
    mediaGeral: geralAverage,
    dimensaoMaisForte: highest,
    dimensaoMaisFraca: lowest,
    dimensoes: data.grupos.map((grupo, index) => ({
      nome: grupo.nome,
      descricao: grupo.descricao,
      resultado: data.resultados.empresa[index]?.value || 0,
      autoAvaliacao: data.resultados.empresa[index]?.autoAvaliacao,
      feedbacks: grupo.feedbacks || []
    })),

    // Metadata
    dataGeracao: formatDateTime(data.dataGeracao),
    totalDimensoes: data.grupos.length
  };
}

/**
 * Get human-readable label for empresa porte
 * @param porte
 */
function getPorteLabel(porte: TPorte): string {
  const labels: Record<TPorte, string> = {
    MICRO_EMPREENDEDOR: "Microempreendedor",
    MICRO_EMPRESA: "Microempresa",
    EMPRESA_PEQUENO_PORTE: "Pequeno Porte",
    EMPRESA_MEDIO_PORTE: "Médio Porte",
    EMPRESA_GRANDE_PORTE: "Grande Porte"
  };
  return labels[porte] || porte;
}
